import { Injectable, inject } from '@angular/core';
import { KeycloakAdminService } from '../keycloak/keycloak.service';
import { ApiService } from '../api/api.service';
import {
  IContact,
  IOrganization,
  AltIOrganization,
  ITag,
  IPagination,
  IDashboardStats,
  ISegmentRule,
} from '../../types/contactModule';
import { UserProfileService } from '../user/user-profile.service';
import {
  map,
  throwError,
  BehaviorSubject,
  Observable,
  tap,
  forkJoin,
  of,
  catchError,
} from 'rxjs';
import { HttpParams, HttpResponse } from '@angular/common/http';
import { getCallingCode, getCountryCode } from '../../../utils/utils';
import {
  IDashboardCampaign,
  IDashboardMessage,
} from '../../types/EngagementModule';
import { SegmentService } from '../segments/segment-service.service';
import {
  IEntitySegmentQuery,
  segmentRuleFormatter,
} from '../../components/organization/segments/segment-utilities';

/** One SMS sent to a contact, as engagement message reports return it. */
export interface IContactMessage {
  uuid?: string;
  messageUuid?: string;
  campaignUuid?: string | null;
  content?: string | null;
  mobileNumber?: string | null;
  deliveryStatus?: string | null;
  status?: string | null;
  timeStamp?: string | null;
  createdAt?: string | null;
}

/** Alpha-2 region for a country name, or undefined when blank or unknown. */
function safeCountryCode(country: string | null | undefined): string | undefined {
  const name = (country ?? '').toString().trim();
  if (!name) return undefined;
  try {
    return getCountryCode(name)?.countryCode;
  } catch {
    return undefined;
  }
}

/** "+267" for a country name, or '' when blank or unknown (never throws). */
function safeCallingCode(country: string | null | undefined): string {
  if (!safeCountryCode(country)) return '';
  try {
    return getCallingCode((country ?? '').toString().trim()) || '';
  } catch {
    return '';
  }
}

@Injectable({
  providedIn: 'root',
})
export class ContactsService {
  keycloakService = inject(KeycloakAdminService);
  apiService = inject(ApiService);
  userService = inject(UserProfileService);
  localSegmentService = inject(SegmentService); //this is the services that has to do with UI manipulations of the segments components
  isFetching = false; // used for GET requests
  isLoading = false; //used for POST,PUT etc requests
  gettingCount = false; // used to control the loaders for getting counts
  private ErrorMsg = '';
  private successMsg = '';

  // timeout IDs for cleanup
  private successTimeoutId?: ReturnType<typeof setTimeout>;
  private errorTimeoutId?: ReturnType<typeof setTimeout>;

  private contacts = new BehaviorSubject<IContact[]>([]);
  private contacts_bulk = new BehaviorSubject<IContact[]>([]);

  private groupContacts = new BehaviorSubject<IContact[]>([]);
  private tags = new BehaviorSubject<ITag[]>([]);
  private tags_bulk = new BehaviorSubject<ITag[]>([]);

  private organizations = new BehaviorSubject<IOrganization[]>([]);
  private organizations_bulk = new BehaviorSubject<IOrganization[]>([]);
  private segments_bulk = new BehaviorSubject<IOrganization[]>([]);

  private segments = new BehaviorSubject<IEntitySegmentQuery[]>([]);
  private contactsCount = new BehaviorSubject<number>(0);
  private segmentsCount = new BehaviorSubject<number>(0);
  private tagsCount = new BehaviorSubject<number>(0);
  private organizationsCount = new BehaviorSubject<number>(0);

  // pages and pagination info
  // CONTACTS
  private contactsPaginationData = new BehaviorSubject<IPagination>({
    totalElements: 0,
    pageSize: 10,
    totalPages: 1,
    pageNumber: 0,
    offSet: 0,
  });
  // Group contacts
  private groupContactsPaginationData = new BehaviorSubject<IPagination>({
    totalElements: 0,
    pageSize: 10,
    totalPages: 1,
    pageNumber: 0,
    offSet: 0,
  });
  // ORGANIZATIONS
  private organizationsPaginationData = new BehaviorSubject<IPagination>({
    totalElements: 0,
    pageSize: 10,
    totalPages: 1,
    pageNumber: 0,
    offSet: 0,
  });
  // TAGS
  private tagsPaginationData = new BehaviorSubject<IPagination>({
    totalElements: 0,
    pageSize: 10,
    totalPages: 1,
    pageNumber: 0,
    offSet: 0,
  });
  private segmentsPaginationData = new BehaviorSubject<IPagination>({
    totalElements: 0,
    pageSize: 10,
    totalPages: 1,
    pageNumber: 0,
    offSet: 0,
  });

  // detailed info
  private detailedContact = new BehaviorSubject<IContact | null>(null);
  private detailedOrganization = new BehaviorSubject<IOrganization | null>(
    null
  );
  private detailedSegment = new BehaviorSubject<IEntitySegmentQuery | null>(
    null
  );
  private detailedTag = new BehaviorSubject<ITag | null>(null);

  constructor() {}

  get organizationId() {
    return this.userService.organizationId;
  }
  serviceName = 'blackkonnectcxpcontacts';

  engagementServiceName = 'blackkonnectcxpengagement';

  // CONTACTS

  createContact(contact: IContact): Observable<any> {
    this.isLoading = true;
    return this.apiService
      .post(
        `${this.serviceName}/api/person-entities/organisations/${this.organizationId}/contacts`,
        {
          firstName: contact.firstName,
          lastName: contact.lastName,
          nickName: '',
          zipCode: contact.zipCode || '',
          city: contact.city,
          country: contact.country,
          birthDate: contact.birthDate,
          countryCode: safeCountryCode(contact.country),
          gender: contact.gender || 'OTHER',
          primaryContactNumber: contact.primaryContactNumber,
          emailAddress: contact.emailAddress,
          customerStage: 'ACTIVE',
          type: 'CONTACT',
          recordStatus: 'ACTIVE',
          organisationId: this.organizationId,
          contactDetails: [],
          customAttributes: [],
          notes: [],
          entityTags: contact.entityTags,
          companyEntities: contact.companyEntities,
        }
      )
      .pipe(
        tap({
          next: () => {
            // Contact created successfully, now fetch the contacts
            this.updateSuccessMsg('Contact added.');
            this.fetchContactsCount();
            this.fetchContacts();
          },
          error: (err) => {
            this.isLoading = false;
            this.updateError(err?.message || 'Failed');
          },
          complete: () => {
            this.isLoading = false;
          },
        })
      );
  }

  // create multiple contacts
  createMultipleContacts(contacts: IContact[]): Observable<any> {
    this.isLoading = true;
    const updatedContacts = contacts.map((contact) => ({
      firstName: contact.firstName,
      lastName: contact.lastName,
      nickName: '',
      zipCode: contact.zipCode || safeCallingCode(contact.country),
      city: contact.city,
      country: contact.country,
      birthDate: contact.birthDate,
      countryCode: safeCountryCode(contact.country),
      gender: contact.gender || 'OTHER',
      primaryContactNumber: contact.primaryContactNumber,
      emailAddress: contact.emailAddress,
      customerStage: 'ACTIVE',
      type: 'CONTACT',
      recordStatus: 'ACTIVE',
      organisationId: this.organizationId,
      contactDetails: [],
      customAttributes: [],
      notes: [],
      entityTags: contact.entityTags,
      companyEntities: contact.companyEntities,
    }));

    return this.apiService
      .post(
        `${this.serviceName}/api/person-entities/organisations/${this.organizationId}/contacts/batch`,
        {
          organisationId: this.organizationId,
          personEntityList: updatedContacts,
        }
      )
      .pipe(
        tap({
          next: () => {
            // Contact created successfully, now fetch the contacts

            this.fetchContactsCount();
            this.fetchContacts();
            this.isLoading = false;
          },
          error: (err) => {
            this.isLoading = false;
          },
          complete: () => {
            this.isLoading = false;
          },
        })
      );
  }

  private updateContacts(contacts: any) {
    this.contacts = contacts;
  }

  // assign multipleContacts to Organizations
  assignMultipleContactsToOrganizations(
    contactIds: string[],
    organizationIds: string[]
  ) {
    this.isLoading = true;
    return this.apiService
      .post(
        `${this.serviceName}/api/person-entities/organisations/${this.organizationId}/contacts/companies/batch`,
        {
          companyUuids: organizationIds,
          contactUuids: contactIds,
        }
      )
      .pipe(
        tap({
          next: () => {
            // Contact created successfully, now fetch the contacts
            this.fetchContactsCount();
            this.fetchContacts();
            this.isLoading = false;
          },
          error: (err) => {
            this.isLoading = false;
          },
          complete: () => {
            this.isLoading = false;
          },
        })
      );
  }

  // assign multiple contacts to tags
  assignMultipleContactsToTags(contactIds: string[], tagIds: string[]) {
    this.isLoading = true;
    return this.apiService
      .post(
        `${this.serviceName}/api/person-entities/organisations/${this.organizationId}/contacts/tags/batch`,
        {
          organisationId: this.organizationId,
          contactUuids: contactIds,
          tagUuids: tagIds,
        }
      )
      .pipe(
        tap({
          next: () => {
            // Contact created successfully, now fetch the contacts
            this.fetchContactsCount();
            this.fetchContacts();
            this.isLoading = false;
          },
          error: (err) => {
            this.isLoading = false;
          },
          complete: () => {
            this.isLoading = false;
          },
        })
      );
  }

  /**
   * The query the contact list last asked for (search, filters, sort, page,
   * size). Refreshes after a create, edit or delete call fetchContacts() with
   * no params; they reuse this so the list keeps its search, sort and page.
   */
  private lastContactsParams: Record<string, any> | null = null;
  /** Only the newest list request may update the list (no last-response-wins race). */
  private contactsRequestId = 0;

  // fetch contacts
  fetchContacts(params?: Record<string, any>) {
    this.isFetching = true;
    const paramLength = Object.keys(params || {}).length;
    if (paramLength) this.lastContactsParams = { ...params };
    const requestId = ++this.contactsRequestId;

    this.apiService
      .get<HttpResponse<any>>(
        `${this.serviceName}/api/v2/person-entities/organisations/${this.organizationId}/contacts`,
        paramLength
          ? params
          : this.lastContactsParams ?? {
              size: this.contactsPaginationData.value.pageSize,
              page: this.contactsPaginationData.value.pageNumber,
            } // Pass query parameters here
      )
      .subscribe({
        next: (data: any) => {
          if (requestId !== this.contactsRequestId) return;
          this.contacts.next(data?.items ?? []);
          if (data?.itemPagination) {
            this.contactsPaginationData.next(data.itemPagination);
          }
        },
        error: (err) => {
          if (requestId !== this.contactsRequestId) return;
          this.isFetching = false;
          this.updateError(err.message);
          return throwError(() => err);
        },
        complete: () => {
          if (requestId === this.contactsRequestId) this.isFetching = false;
        },
      });
  }

  /**
   * One page of the contact list with no side effects on shared state: no
   * subjects, no loading flags. Used by the CSV export, which pages through
   * the whole list in the background.
   */
  listContactsPage(
    params: Record<string, any>
  ): Observable<{ items: IContact[]; itemPagination: IPagination }> {
    return this.apiService
      .get<any>(
        `${this.serviceName}/api/v2/person-entities/organisations/${this.organizationId}/contacts`,
        params
      )
      .pipe(
        map((data: any) => ({
          items: (data?.items ?? []) as IContact[],
          itemPagination: data?.itemPagination as IPagination,
        }))
      );
  }

  /** A page of organizations for pickers and filters. No shared state. */
  searchOrganizations(
    params: Record<string, any>
  ): Observable<{ items: IOrganization[]; itemPagination: IPagination }> {
    return this.apiService
      .get<any>(
        `${this.serviceName}/api/v2/company-entities/organisations/${this.organizationId}/companies`,
        params
      )
      .pipe(
        map((data: any) => ({
          items: (data?.items ?? []) as IOrganization[],
          itemPagination: data?.itemPagination as IPagination,
        }))
      );
  }

  /** A page of tags for pickers and filters. No shared state. */
  searchTags(
    params: Record<string, any>
  ): Observable<{ items: ITag[]; itemPagination: IPagination }> {
    return this.apiService
      .get<any>(
        `${this.serviceName}/api/v2/entity-tags/organisations/${this.organizationId}`,
        params
      )
      .pipe(
        map((data: any) => ({
          items: (data?.items ?? []) as ITag[],
          itemPagination: data?.itemPagination as IPagination,
        }))
      );
  }

  /**
   * The newest SMS sent to one contact, from engagement message reports.
   * A contact uuid belongs to one organisation, so no organisation filter is
   * added (rows that lack the organisation field would otherwise vanish).
   */
  fetchContactMessages(
    contactUuid: string,
    size: number = 5
  ): Observable<IContactMessage[]> {
    return this.apiService
      .get<any>(`${this.engagementServiceName}/api/engagement-message-reports`, {
        'contactUuid.equals': contactUuid,
        sort: 'id,desc',
        page: 0,
        size,
      })
      .pipe(map((rows: any) => (Array.isArray(rows) ? rows : rows?.items ?? [])));
  }

  /** How many SMS a contact has been sent, optionally only in some delivery states. */
  countContactMessages(
    contactUuid: string,
    deliveryStates?: string[]
  ): Observable<number> {
    return this.apiService
      .get<number>(
        `${this.engagementServiceName}/api/engagement-message-reports/count`,
        {
          'contactUuid.equals': contactUuid,
          ...(deliveryStates?.length ? { 'deliveryStatus.in': deliveryStates } : {}),
        }
      )
      .pipe(map((value) => Number(value) || 0));
  }

  // fetch contacts fetchContactsBulk
  fetchContactsBulk(params?: Record<string, any>) {
    this.isFetching = true;
    const paramLength = Object.keys(params || {}).length;

    this.apiService
      .get<HttpResponse<any>>(
        `${this.serviceName}/api/v2/person-entities/organisations/${this.organizationId}/contacts`,
        paramLength
          ? params
          : {
              size: this.contactsPaginationData.value.pageSize,
              page: this.contactsPaginationData.value.pageNumber,
            } // Pass query parameters here
      )
      .subscribe({
        next: (data: any) => {
          this.contacts_bulk.next(data.items);
        },
        error: (err) => {
          this.isFetching = false;
          this.updateError(err.message);
          return throwError(() => err);
        },
        complete: () => {
          this.isFetching = false;
        },
      });
  }

  // fetch contacts
  fetchGroupContacts(
    groupType: 'organization' | 'tag' | 'segment',
    groupId: string | number,
    params?: Record<string, any>
  ) {
    this.isFetching = true;
    const paramLength = Object.keys(params || {}).length;

    // let queryParams = new HttpParams();
    // if (params && paramLength) {
    //   Object.keys(params).forEach((key) => {
    //     queryParams = queryParams.set(key, params[key].toString());
    //   });
    // }
    // const groupParam = (): Record<string, any> => {
    //   switch (groupType) {
    //     case 'organization':
    //       return { 'companyEntityId.equals': groupId };
    //     case 'tag':
    //       return { 'entityTagId.equals': groupId };
    //     case 'segment':
    //       // return {'entitySegmentId.equals': groupId,};
    //       return {};
    //     default:
    //       return { 'companyEntityId.equals': groupId };
    //   }
    // };
    const updatedParams = { ...params };

    this.apiService
      .get(
        this.groupContactsEndpoint(groupType, groupId),
        updatedParams // Pass query parameters here,
      )
      .subscribe({
        next: (data: any) => {
          // this.updateContacts(data);
          this.groupContacts.next(data.items);
          this.groupContactsPaginationData.next(data.itemPagination);
        },
        error: (err) => {
          this.isFetching = false;
          this.updateError(err.message);
          return throwError(() => err);
        },
        complete: () => {
          this.isFetching = false;
        },
      });
  }

  private groupContactsEndpoint(
    groupType: 'organization' | 'tag' | 'segment',
    groupId: string | number
  ): string {
    switch (groupType) {
      case 'segment':
        return `${this.serviceName}/api/v2/person-entities/organisations/${this.organizationId}/contacts/segments/${groupId}`;
      case 'organization':
        return `${this.serviceName}/api/v2/person-entities/organisations/${this.organizationId}/contacts/companies/${groupId}`;
      case 'tag':
        return `${this.serviceName}/api/person-entities/organisations/${this.organizationId}/contacts/tags/${groupId}`;
    }
  }

  private groupSearchToken = 0;

  // Run a search across multiple contact fields in parallel, dedupe by uuid,
  // and update the group contacts subject with the merged result. Stale
  // responses are dropped if a newer search has started.
  searchGroupContactsAcrossFields(
    groupType: 'organization' | 'tag' | 'segment',
    groupId: string | number,
    searchTerm: string,
    baseParams: Record<string, any> = {}
  ) {
    const normalized = searchTerm.trim();
    if (!normalized) {
      this.fetchGroupContacts(groupType, groupId, baseParams);
      return;
    }

    const token = ++this.groupSearchToken;
    this.isFetching = true;

    const endpoint = this.groupContactsEndpoint(groupType, groupId);
    const { page, size, ...rest } = baseParams;
    const fallbackSize = size ?? 10;
    const sharedParams = { ...rest, page: 0, size: Math.max(fallbackSize, 100) };

    const fieldFilters: Record<string, string>[] = [
      { 'fullName.contains': normalized },
      { 'primaryContactNumber.contains': normalized },
      { 'emailAddress.contains': normalized },
      { 'country.contains': normalized },
    ];
    if (['MALE', 'FEMALE', 'OTHER'].includes(normalized.toUpperCase())) {
      fieldFilters.push({ 'gender.equals': normalized.toUpperCase() });
    }

    const requests = fieldFilters.map((filter) =>
      this.apiService
        .get<any>(endpoint, { ...sharedParams, ...filter })
        .pipe(catchError(() => of({ items: [] })))
    );

    forkJoin(requests).subscribe({
      next: (responses: any[]) => {
        if (token !== this.groupSearchToken) return;
        const merged = new Map<string, IContact>();
        for (const r of responses) {
          for (const item of r?.items ?? []) {
            if (item?.uuid) merged.set(item.uuid, item);
          }
        }
        // Some group endpoints silently ignore unsupported filter params
        // and return the full member list. Re-check each merged contact
        // against the search term client-side so we never surface rows
        // that don't actually match.
        const items = Array.from(merged.values()).filter((c) =>
          this.contactMatchesSearch(c, normalized)
        );
        this.groupContacts.next(items);
        this.groupContactsPaginationData.next({
          totalElements: items.length,
          pageSize: items.length || fallbackSize,
          totalPages: 1,
          pageNumber: 0,
          offSet: 0,
        });
        this.isFetching = false;
      },
      error: (err) => {
        if (token !== this.groupSearchToken) return;
        this.isFetching = false;
        this.updateError(err?.message ?? 'Search failed');
      },
    });
  }

  private contactMatchesSearch(contact: IContact, term: string): boolean {
    const needle = term.trim().toLowerCase();
    if (!needle) return true;

    const fullName =
      contact.fullName?.toLowerCase() ??
      `${contact.firstName ?? ''} ${contact.lastName ?? ''}`
        .trim()
        .toLowerCase();
    if (fullName.includes(needle)) return true;

    const phone = contact.primaryContactNumber?.toString().toLowerCase();
    if (phone?.includes(needle)) return true;

    if (contact.emailAddress?.toLowerCase().includes(needle)) return true;
    if (contact.country?.toLowerCase().includes(needle)) return true;
    if (contact.gender?.toLowerCase() === needle) return true;

    return false;
  }

  fetchContactsCount() {
    this.gettingCount = true;
    this.apiService
      .get(
        `${this.serviceName}/api/person-entities/organisations/${this.organizationId}/count`
      )
      .subscribe({
        next: (data: any) => {
          // this.updateContacts(data);
          this.contactsCount.next(data);
          this.gettingCount = false;
        },
        error: (err) => {
          this.gettingCount = false;
          this.updateError(err.message);
          return throwError(() => err);
        },
        complete: () => {
          // Counts never own the list skeleton.
          this.gettingCount = false;
        },
      });
  }

  updateContact(contact: IContact, contactId: string) {
    this.isLoading = true;
    return this.apiService
      .put(
        `${this.serviceName}/api/person-entities/organisations/${this.organizationId}/contacts/${contactId}`,
        {
          ...contact,
          // Keep the stored region when the country is blank or unknown.
          countryCode: safeCountryCode(contact.country) ?? contact.countryCode,
        }
      )
      .pipe(
        // Perform side effects like updating local state, fetching updated contacts, etc.
        tap({
          next: () => {
            this.updateSuccessMsg('Changes saved.');
            this.fetchContacts(); // Optionally fetch updated contacts after deletion
            this.isLoading = false;
          },
          error: (error) => {
            this.isLoading = false;
            this.updateError(error?.message || 'Update failed');
          },
          complete: () => {
            this.isLoading = false;
          },
        })
      );
  }

  // delete a contact via api
  deleteContact(id: string): Observable<any> {
    this.isLoading = true;
    return this.apiService
      .delete(
        `${this.serviceName}/api/person-entities/organisations/${this.organizationId}/contacts/${id}`
      )
      .pipe(
        // Perform side effects like updating local state, fetching updated contacts, etc.
        tap({
          next: () => {
            this.fetchContactsCount();
            this.fetchContacts(); // Optionally fetch updated contacts after deletion
            this.isLoading = false;
          },
          error: () => {
            this.isLoading = false;
          },
          complete: () => {
            this.isLoading = false;
          },
        })
      );
  }

  // delete batch of contacts
  deleteSelectedContacts(contactIds: string[]) {
    this.isLoading = true;
    return this.apiService
      .deleteWithBody(
        `${this.serviceName}/api/person-entities/organisations/${this.organizationId}/contacts/batch`,
        {
          organisationId: this.organizationId,
          contactUuids: contactIds,
        }
      )
      .pipe(
        // Perform side effects like updating local state, fetching updated contacts, etc.
        tap({
          next: () => {
            this.updateSuccessMsg('Contacts deleted.');
            this.fetchContactsCount();
            this.fetchContacts(); // Optionally fetch updated contacts after deletion
            this.isLoading = false;
          },
          error: () => {
            this.isLoading = false;
          },
          complete: () => {
            this.isLoading = false;
          },
        })
      );
  }

  get getContacts() {
    return this.contacts.asObservable();
  }
  get getContactsUuid() {
    return this.contacts_bulk.asObservable();
  }

  get getContactsPaginationData() {
    return this.contactsPaginationData.asObservable();
  }

  get getGroupContacts() {
    return this.groupContacts.asObservable();
  }

  get getGroupContactsPaginationData() {
    return this.groupContactsPaginationData.asObservable();
  }

  get getContactLength() {
    return this.contactsCount.asObservable();
  }

  // ------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------

  // TAGS
  fetchTags(params?: Record<string, any>) {
    this.isFetching = true;
    const paramLength = Object.keys(params || {}).length;

    this.apiService
      .get(
        `${this.serviceName}/api/v2/entity-tags/organisations/${this.organizationId}`,
        paramLength
          ? params
          : {
              size: this.tagsPaginationData.value.pageSize,
              page: this.tagsPaginationData.value.pageNumber,
            } // Pass query parameters here
      )
      .subscribe({
        next: (data: any) => {
          this.fetchContactsCount();
          this.tags.next(data.items);
          this.tagsPaginationData.next(data.itemPagination);
        },
        error: (err) => {
          this.isFetching = false;
          this.updateError(err.message);
          return throwError(() => err);
        },
        complete: () => {
          this.isFetching = false;
        },
      });
  }

  // TAGS
  fetchTagsBulk(params?: Record<string, any>) {
    this.isFetching = true;
    const paramLength = Object.keys(params || {}).length;

    this.apiService
      .get(
        `${this.serviceName}/api/v2/entity-tags/organisations/${this.organizationId}`,
        paramLength
          ? params
          : {
              size: this.tagsPaginationData.value.pageSize,
              page: this.tagsPaginationData.value.pageNumber,
            } // Pass query parameters here
      )
      .subscribe({
        next: (data: any) => {
          this.tags_bulk.next(data.items);
        },
        error: (err) => {
          this.isFetching = false;
          this.updateError(err.message);
          return throwError(() => err);
        },
        complete: () => {
          this.isFetching = false;
        },
      });
  }

  // fetch detailed tag
  fetchDetailedTag(id: string) {
    this.isFetching = true;
    this.apiService
      .get(
        `${this.serviceName}/api/entity-tags/organisations/${this.organizationId}/entity-tags/${id}`
      )
      .subscribe({
        next: (data: any) => {
          // this.updateContacts(data);
          this.detailedTag.next(data);
        },
        error: (err) => {
          this.isFetching = false;
          this.updateError(err.message);
          return throwError(() => err);
        },
        complete: () => {
          this.isFetching = false;
        },
      });
  }

  fetchTagsCount() {
    this.gettingCount = true;
    this.apiService
      .get(
        `${this.serviceName}/api/entity-tags/organisations/${this.organizationId}/count`
      )
      .subscribe({
        next: (data: any) => {
          // this.updateContacts(data);
          this.tagsCount.next(data);
          this.gettingCount = false;
        },
        error: (err) => {
          this.gettingCount = false;
          this.updateError(err.message);
          return throwError(() => err);
        },
        complete: () => {
          // Counts never own the list skeleton.
          this.gettingCount = false;
        },
      });
  }

  // you have to subscribe to it in the component
  fetchTagsContactsCount(tagId: string) {
    this.gettingCount = true;
    return this.apiService.get(
      `${this.serviceName}/api/entity-tags/organisations/${this.organizationId}/tags/${tagId}/count`
    );
  }

  // remove the contacts that belong to a Tag
  removeTagContacts(tagUuid: string, contactUuids: string[]) {
    this.isLoading = true;
    return this.apiService
      .put(
        `${this.serviceName}/api/entity-tags/organisations/${this.organizationId}/entity-tags/${tagUuid}/remove/contacts`,
        {
          organisationId: this.organizationId,
          contactUuids: contactUuids,
        }
      )
      .pipe(
        // Perform side effects like updating local state, fetching updated contacts, etc.
        tap({
          next: () => {
            this.isLoading = false;
          },
          error: () => {
            this.isLoading = false;
          },
          complete: () => {
            this.isLoading = false;
          },
        })
      );
  }

  // create tags
  createTag(tag: ITag): Observable<any> {
    this.isLoading = true;
    return this.apiService
      .post(
        `${this.serviceName}/api/entity-tags/organisations/${this.organizationId}`,
        {
          name: tag.name,
          description: tag.description,
          organisationId: this.organizationId,
        }
      )
      .pipe(
        tap({
          next: () => {
            this.fetchTagsCount();
            this.fetchTags();
          },
          error: (err) => {
            this.isLoading = false;
            // console.error('Error creating contact:', err);
          },
          complete: () => {
            this.isLoading = false;
          },
        })
      );
  }

  // Update a Tag
  updateTag(tag: ITag, tagUuid: string) {
    this.isLoading = true;
    return this.apiService
      .put(
        `${this.serviceName}/api/entity-tags/organisations/${this.organizationId}/entity-tags/${tagUuid}`,
        {
          ...tag,
        }
      )
      .pipe(
        // Perform side effects like updating local state, fetching updated contacts, etc.
        tap({
          next: () => {
            this.fetchTags(); // Optionally fetch updated contacts after deletion
            this.isLoading = false;
          },
          error: (error) => {
            this.isLoading = false;
            // console.error('Error updating contact', error);
          },
          complete: () => {
            this.isLoading = false;
          },
        })
      );
  }

  // delete a tag via api
  deleteTag(id: string): Observable<any> {
    this.isLoading = true;
    return this.apiService
      .delete(`${this.serviceName}/api/entity-tags/${id}`)
      .pipe(
        // Perform side effects like updating local state, fetching updated contacts, etc.
        tap({
          next: () => {
            this.fetchTagsCount();
            this.fetchTags(); // refresh the list after deletion
            this.isLoading = false;
          },
          error: (err) => {
            this.isLoading = false;
            this.updateError(err.message);
            return throwError(() => err);
          },
          complete: () => {
            this.isLoading = false;
          },
        })
      );
  }

  // delete batch of Tags
  deleteSelectedTags(tagUuids: string[]) {
    this.isLoading = true;
    return this.apiService
      .put(
        `${this.serviceName}/api/entity-tags/organisations/${this.organizationId}/entity-tags/batch/delete`,
        {
          organisationId: this.organizationId,
          tagUuids: tagUuids,
        }
      )
      .pipe(
        // Perform side effects like updating local state, fetching updated contacts, etc.
        tap({
          next: () => {
            this.updateSuccessMsg('Tags deleted.');
            this.fetchTagsCount();
            this.fetchTags(); // Optionally fetch updated contacts after deletion
            this.isLoading = false;
          },
          error: () => {
            this.isLoading = false;
          },
          complete: () => {
            this.isLoading = false;
          },
        })
      );
  }

  get getTags() {
    return this.tags.asObservable();
  }

  get getTagsBulk() {
    return this.tags_bulk.asObservable();
  }
  get getDetailedTag() {
    return this.detailedTag.asObservable();
  }

  get getTagsPaginationData() {
    return this.tagsPaginationData.asObservable();
  }

  get getTagsLength() {
    return this.tagsCount.asObservable();
  }

  // -----------------------------------------------------------------------------------------

  // ORGANIZATIONS
  // get all organizations
  fetchOrganizations(params?: Record<string, any>) {
    this.isFetching = true;
    const paramLength = Object.keys(params || {}).length;
    this.apiService
      // .get(`${this.serviceName}/api/contacts-organisation-profiles`)
      .get(
        `${this.serviceName}/api/v2/company-entities/organisations/${this.organizationId}/companies`,
        paramLength
          ? params
          : {
              size: this.organizationsPaginationData.value.pageSize,
              page: this.organizationsPaginationData.value.pageNumber,
            }
      ) //temporary
      .subscribe({
        next: (data: any) => {
          //  console.log('data :>> ', data);
          this.fetchOrganizationCount();
          this.organizations.next(data.items);
          this.organizationsPaginationData.next(data.itemPagination);
        },
        error: (err) => {
          this.isFetching = false;
          this.updateError(err.message);
          return throwError(() => err);
        },
        complete: () => {
          this.isFetching = false;
        },
      });
  }

  // get all organizations
  fetchOrganizationsBulk(params?: Record<string, any>) {
    this.isFetching = true;
    const paramLength = Object.keys(params || {}).length;
    this.apiService
      // .get(`${this.serviceName}/api/contacts-organisation-profiles`)
      .get(
        `${this.serviceName}/api/v2/company-entities/organisations/${this.organizationId}/companies`,
        paramLength
          ? params
          : {
              size: this.organizationsPaginationData.value.pageSize,
              page: this.organizationsPaginationData.value.pageNumber,
            }
      ) //temporary
      .subscribe({
        next: (data: any) => {
          this.organizations_bulk.next(data.items);
        },
        error: (err) => {
          this.isFetching = false;
          this.updateError(err.message);
          return throwError(() => err);
        },
        complete: () => {
          this.isFetching = false;
        },
      });
  }

  fetchOrganizationCount() {
    this.gettingCount = true;
    this.apiService
      .get(
        `${this.serviceName}/api/company-entities/organisations/${this.organizationId}/count`
      )
      .subscribe({
        next: (data: any) => {
          // this.updateContacts(data);
          this.organizationsCount.next(data);
          this.gettingCount = false;
        },
        error: (err) => {
          this.gettingCount = false;
          this.updateError(err.message);
          return throwError(() => err);
        },
        complete: () => {
          // Counts never own the list skeleton.
          this.gettingCount = false;
        },
      });
  }

  // you have to subscribe to it in the component
  fetchOrganizationContactsCount(organnizationUuid: string) {
    this.gettingCount = true;
    return this.apiService.get(
      `${this.serviceName}/api/person-entities/organisations/${this.organizationId}/contacts/companies/${organnizationUuid}/count`
    );
  }

  fetchOrganizationDetail(id: string) {
    this.isFetching = true;
    this.apiService
      .get(
        `${this.serviceName}/api/company-entities/organisations/${this.organizationId}/companies/${id}`
      )
      // .get(`${this.serviceName}/api/company-entities/organisations/${this.organizationId}/contacts/${id}`)
      .subscribe({
        next: (data: any) => {
          // this.updateContacts(data);
          this.detailedOrganization.next(data);
        },
        error: (err) => {
          this.isFetching = false;
          this.updateError(err.message);
          return throwError(() => err);
        },
        complete: () => {
          this.isFetching = false;
        },
      });
  }

  // endpoint to fetch the contacts of a specific organization.... Not done, DON'T USE!!!
  fetchOrganizationContacts() {
    this.isFetching = true;
    this.apiService
      .get(
        `${this.serviceName}/api/company-entities/organisations/${this.organizationId}/companies`
      ) //temporary
      .subscribe({
        next: (data: any) => {
          // this.updateContacts(data);
          this.fetchOrganizationCount();
          this.organizations.next(data);
        },
        error: (err) => {
          this.isFetching = false;
          this.updateError(err.message);
          return throwError(() => err);
        },
        complete: () => {
          this.isFetching = false;
        },
      });
  }

  // remove the contacts that belong to an organization
  removeOrganizationContacts(organizationUuid: string, contactUuids: string[]) {
    this.isLoading = true;
    return this.apiService
      .put(
        `${this.serviceName}/api/company-entities/organisations/${this.organizationId}/companies/${organizationUuid}/remove/contacts`,
        {
          companyUuids: [this.organizationId],
          contactUuids: contactUuids,
        }
      )
      .pipe(
        // Perform side effects like updating local state, fetching updated contacts, etc.
        tap({
          next: () => {
            this.isLoading = false;
          },
          error: () => {
            this.isLoading = false;
          },
          complete: () => {
            this.isLoading = false;
          },
        })
      );
  }

  // create organization
  createOrganization(organization: IOrganization): Observable<any> {
    this.isLoading = true;
    return this.apiService
      .post(
        `${this.serviceName}/api/company-entities/organisations/${this.organizationId}/companies`,
        // `${this.serviceName}/api/company-entities`,
        {
          name: organization.name,
          organisationId: this.organizationId,
          description: organization.description,
          country: organization.country,
          countryCode: getCountryCode(organization.country!)?.countryCode,
          zipCode: getCallingCode(organization.country!),
          city: organization.city,
          customerStage: 'PROSPECTIVE',
          region: organization.region,
          industry: organization.industry,
        }
      )
      .pipe(
        tap({
          next: () => {
            this.updateSuccessMsg('Organization created.');
            this.fetchOrganizations();
          },
          error: (err) => {
            this.isLoading = false;
            this.updateError(err?.message || 'Creation Failed');
          },
          complete: () => {
            this.isLoading = false;
          },
        })
      );
  }

  // delete an organization via api
  updateOrganization(
    organization: IOrganization,
    uuid: string
  ): Observable<any> {
    this.isLoading = true;
    return this.apiService
      .put(
        `${this.serviceName}/api/company-entities/organisations/${this.organizationId}/companies/${uuid}`,
        organization
      )
      .pipe(
        // Perform side effects like updating local state, fetching updated contacts, etc.
        tap({
          next: () => {
            this.updateSuccessMsg('Changes saved.');
            this.fetchOrganizations(); // Optionally fetch updated contacts after deletion
            this.isLoading = false;
          },
          error: (err) => {
            this.isLoading = false;
            this.updateError(err.message || 'Update Failed');
            return throwError(() => err);
          },
          complete: () => {
            this.isLoading = false;
          },
        })
      );
  }

  // delete an organization via api
  deleteOrganization(id: string): Observable<any> {
    this.isLoading = true;
    return this.apiService
      .delete(`${this.serviceName}/api/company-entities/${id}`)
      .pipe(
        // Perform side effects like updating local state, fetching updated contacts, etc.
        tap({
          next: () => {
            this.updateSuccessMsg('Organization deleted.');
            this.fetchOrganizations(); // Optionally fetch updated contacts after deletion
            this.isLoading = false;
          },
          error: (err) => {
            this.isLoading = false;
            this.updateError(err.message || 'Delete Failed');
            return throwError(() => err);
          },
          complete: () => {
            this.isLoading = false;
          },
        })
      );
  }

  // delete batch of Organizations
  deleteSelectedOrganizations(organizationUuids: string[]) {
    this.isLoading = true;
    return this.apiService
      .put(
        `${this.serviceName}/api/company-entities/organisations/${this.organizationId}/companies/batch/delete`,
        {
          organisationId: this.organizationId,
          companyUuids: organizationUuids,
        }
      )
      .pipe(
        // Perform side effects like updating local state, fetching updated contacts, etc.
        tap({
          next: () => {
            this.updateSuccessMsg('Organizations deleted.');
            this.fetchOrganizationCount();
            this.fetchOrganizations(); // Optionally fetch updated contacts after deletion
            this.isLoading = false;
          },
          error: () => {
            this.isLoading = false;
          },
          complete: () => {
            this.isLoading = false;
          },
        })
      );
  }

  get getOrganizations() {
    return this.organizations.asObservable();
  }

  get getOrganizationsBulk() {
    return this.organizations_bulk.asObservable();
  }

  get organizationDetail() {
    return this.detailedOrganization.asObservable();
  }

  get getOrganizationsCount() {
    return this.organizationsCount.asObservable();
  }

  get getOrganizationsPaginationData() {
    return this.organizationsPaginationData.asObservable();
  }

  // -----------------------------------------------------------------------------------------

  // SEGMENTS
  fetchSegments(params?: Record<string, any>) {
    this.isFetching = true;
    const paramLength = Object.keys(params || {}).length;

    this.apiService
      .get(
        `${this.serviceName}/api/entity-segments/organisations/${this.organizationId}`,
        paramLength
          ? params
          : {
              size: this.segmentsPaginationData.value.pageSize,
              page: this.segmentsPaginationData.value.pageNumber,
            } // Pass query parameters here
      )
      // .get(`blackkonnectcxpengagement/api/engagement-segments`) //temporary
      .subscribe({
        next: (data: any) => {
          // this.updateContacts(data);
          this.segments.next(data.items);
          // put the updating of the segments pagination data here
          this.segmentsPaginationData.next(data.itemPagination);
        },
        error: (err) => {
          this.isFetching = false;
          this.updateError(err.message);
          return throwError(() => err);
        },
        complete: () => {
          this.isFetching = false;
        },
      });
  }

  fetchBulkSegments(params?: Record<string, any>) {
    this.isFetching = true;
    const paramLength = Object.keys(params || {}).length;

    this.apiService
      .get(
        `${this.serviceName}/api/entity-segments/organisations/${this.organizationId}`,
        paramLength
          ? params
          : {
              size: this.segmentsPaginationData.value.pageSize,
              page: this.segmentsPaginationData.value.pageNumber,
            } // Pass query parameters here
      )
      // .get(`blackkonnectcxpengagement/api/engagement-segments`) //temporary
      .subscribe({
        next: (data: any) => {
          // this.updateContacts(data);
          // The endpoint pages its results; consumers expect the list itself.
          this.segments_bulk.next(Array.isArray(data) ? data : data?.items ?? []);
          // put the updating of the segments pagination data here
          // this.segmentsPaginationData.next(data.itemPagination)
        },
        error: (err) => {
          this.isFetching = false;
          this.updateError(err.message);
          return throwError(() => err);
        },
        complete: () => {
          this.isFetching = false;
        },
      });
  }

  // get the segment detail
  fetchSegmentDetails(segmentId: string) {
    this.isFetching = true;
    this.apiService
      .get(
        `${this.serviceName}/api/entity-segments/organisations/${this.organizationId}/entity-segments/${segmentId}`
      )
      // .get(`blackkonnectcxpengagement/api/engagement-segments`) //temporary
      .subscribe({
        next: (data: any) => {
          // this.updateContacts(data);
          this.detailedSegment.next(data);
        },
        error: (err) => {
          this.isFetching = false;
          this.updateError(err.message);
          return throwError(() => err);
        },
        complete: () => {
          this.isFetching = false;
        },
      });
  }

  // you have to subscribe to it in the component FETCH THE CONTACTS THAT BELONG TO A SEGMENT
  fetchSegmentContactsCount(segmentId: string) {
    this.gettingCount = true;
    return this.apiService.get(
      `${this.serviceName}/api/person-entities/organisations/${this.organizationId}/entity-segments/${segmentId}/contacts/count`
    );
  }

  fetchSegmentsCount() {
    this.gettingCount = true;
    this.apiService
      .get(
        `${this.serviceName}/api/entity-segments/organisations/${this.organizationId}/count`
      )
      .subscribe({
        next: (data: any) => {
          // this.updateContacts(data);
          this.segmentsCount.next(data);
          this.gettingCount = false;
        },
        error: (err) => {
          this.gettingCount = false;
          this.updateError(err.message);
          return throwError(() => err);
        },
        complete: () => {
          // Counts never own the list skeleton.
          this.gettingCount = false;
        },
      });
  }

  // create segment
  createSegment(): Observable<any> {
    this.isLoading = true;
    const segmentInformation = this.localSegmentService.segmentInformation;
    const segmentRules = this.localSegmentService.allSegmentRules;
    const payload = {
      name: segmentInformation.segmentName,
      description: segmentInformation.description,
      entitySegmentQueryFilters: segmentRules.map((rule: ISegmentRule) =>
        segmentRuleFormatter(rule)
      ),
    };
    return this.apiService
      .post(
        `${this.serviceName}/api/entity-segments/organisations/${this.organizationId}`,
        // `${this.serviceName}/api/company-entities`,
        payload
      )
      .pipe(
        tap({
          next: () => {
            this.isLoading = false;
            this.localSegmentService.clearSegmentInformation();
            this.fetchSegmentsCount();
            this.fetchSegments();
          },
          error: (err) => {
            this.isLoading = false;
          },
          complete: () => {
            this.isLoading = false;
          },
        })
      );
  }

  // delete a segment via api
  deleteSegment(id: string): Observable<any> {
    this.isLoading = true;
    return this.apiService
      .delete(
        `${this.serviceName}/api/entity-segments/organisations/${this.organizationId}/entity-segments/${id}`
      )
      .pipe(
        // Perform side effects like updating local state, fetching updated segments, etc.
        tap({
          next: () => {
            this.fetchSegmentsCount();
            this.fetchSegments(); // Optionally fetch updated segments after deletion
            this.isLoading = false;
          },
          error: () => {
            this.isLoading = false;
          },
          complete: () => {
            this.isLoading = false;
          },
        })
      );
  }

  // delete batch of Organizations
  deleteSelectedSegments(segmentUuids: string[]) {
    this.isLoading = true;
    return this.apiService
      .put(
        `${this.serviceName}/api/entity-segments/organisations/${this.organizationId}/entity-segments/batch/delete`,
        {
          organisationId: this.organizationId,
          segmentUuids: segmentUuids,
        }
      )
      .pipe(
        // Perform side effects like updating local state, fetching updated contacts, etc.
        tap({
          next: () => {
            this.updateSuccessMsg('Segments deleted.');
            this.fetchSegmentsCount();
            this.fetchSegments(); // Optionally fetch updated contacts after deletion
            this.isLoading = false;
          },
          error: () => {
            this.isLoading = false;
          },
          complete: () => {
            this.isLoading = false;
          },
        })
      );
  }

  // create segment
  updateSegment(
    segmentUuid: string | number,
    selectedSegment: IEntitySegmentQuery
  ): Observable<any> {
    this.isLoading = true;
    const segmentInformation = this.localSegmentService.segmentInformation;
    const segmentRules = this.localSegmentService.allSegmentRules;
    const payload = {
      name: segmentInformation.segmentName,
      description: segmentInformation.description,
      id: selectedSegment.id,
      uuid: selectedSegment.uuid,
      entitySegmentQueryFilters: segmentRules.map((rule: ISegmentRule) =>
        segmentRuleFormatter(rule)
      ),
    };
    return this.apiService
      .put(
        // `${this.serviceName}/api/entity-segments/organisations/${this.organizationId}/entity-segments/${segmentUuid}`,
        `${this.serviceName}/api/entity-segments/organisations/${this.organizationId}/entity-segments/${segmentUuid}`,
        payload
      )
      .pipe(
        tap({
          next: () => {
            this.isLoading = false;
            this.fetchSegments();
          },
          error: (err) => {
            this.isLoading = false;
          },
          complete: () => {
            this.isLoading = false;
          },
        })
      );
  }

  get getSegments() {
    return this.segments.asObservable();
  }
  get getBulkSegments() {
    return this.segments_bulk.asObservable();
  }

  get getSegmentsCount() {
    return this.segmentsCount.asObservable();
  }
  get getSegmentDetail() {
    return this.detailedSegment.asObservable();
  }
  get getSegmentPaginationData() {
    return this.segmentsPaginationData.asObservable();
  }

  // ------------------------------------------------------------------------------------------------
  // DASHBOARD

  dashboardStats = new BehaviorSubject<IDashboardStats>({
    campaignsCount: 0,
    contactsCount: 0,
    usersCount: 0,
    pendingMessagesCount: 0,
  });

  dashboardSummaryCampaigns = new BehaviorSubject<IDashboardCampaign[]>([]);

  dashboardSummaryMessages = new BehaviorSubject<IDashboardMessage[]>([]);

  dashboardLoading: boolean = false;

  // get dashboard Stats
  /**
   * The dashboard's stats and summaries. `silent` is its background refresh:
   * no loading state and no error message, so a failed poll keeps what is shown.
   */
  fetchDashboardStats({ silent = false } = {}) {
    this.loadDashboard(`blackkonnectcxpengagement/api/dashboard/${this.organizationId}/stats`, undefined, this.dashboardStats, silent);
  }

  // fetch summary campaigns, newest first
  fetchDashboardSummaryCampaigns({ silent = false } = {}) {
    this.loadDashboard(
      `blackkonnectcxpengagement/api/dashboard/${this.organizationId}/summary/campaigns`,
      // The endpoint is a Spring Pageable with no default order; without a
      // sort it returns six arbitrary (often the oldest) rows.
      { size: 6, sort: ['createdAt,desc', 'id,desc'] },
      this.dashboardSummaryCampaigns,
      silent
    );
  }

  // fetch summary messages, newest first
  fetchDashboardSummaryMessages({ silent = false } = {}) {
    this.loadDashboard(
      `blackkonnectcxpengagement/api/dashboard/${this.organizationId}/summary/messages`,
      { size: 6, sort: ['createdAt,desc', 'id,desc'] },
      this.dashboardSummaryMessages,
      silent
    );
  }

  private loadDashboard(route: string, params: Record<string, unknown> | undefined, target: BehaviorSubject<any>, silent: boolean) {
    if (!silent) this.dashboardLoading = true;
    this.apiService.get(route, params).subscribe({
      next: (data: any) => {
        if (!silent) this.dashboardLoading = false;
        target.next(data);
      },
      error: (err) => {
        if (silent) return;
        this.dashboardLoading = false;
        this.updateError(err.message);
      },
    });
  }


  get getDashboardStats() {
    return this.dashboardStats.asObservable();
  }

  get getDashboardSummaryCampaigns() {
    return this.dashboardSummaryCampaigns.asObservable();
  }

  get getDashboardSummaryMessages() {
    return this.dashboardSummaryMessages.asObservable();
  }

  get getDashboardLoading() {
    return this.dashboardLoading;
  }

  private updateError(msg: string) {
    // Clear existing timeout before setting a new one
    if (this.errorTimeoutId) {
      clearTimeout(this.errorTimeoutId);
    }
    this.ErrorMsg = msg;
    this.errorTimeoutId = setTimeout(() => {
      this.ErrorMsg = '';
      this.errorTimeoutId = undefined;
    }, 5000);
  }

  private updateSuccessMsg(msg: string) {
    // Clear existing timeout before setting a new one
    if (this.successTimeoutId) {
      clearTimeout(this.successTimeoutId);
    }
    this.successMsg = msg;
    this.successTimeoutId = setTimeout(() => {
      this.successMsg = '';
      this.successTimeoutId = undefined;
    }, 5000);
  }

  /** Flash a confirmation in the contacts shell's feedback card. */
  showSuccess(msg: string) {
    this.updateSuccessMsg(msg);
  }

  /** Flash an error in the contacts shell's feedback card. */
  showError(msg: string) {
    this.updateError(msg);
  }

  get getSuccessMsg() {
    return this.successMsg;
  }

  get getErrorMsg() {
    return this.ErrorMsg;
  }
}
